import {createAction, createAsyncThunk} from '@reduxjs/toolkit'

import {isEmpty} from '@shared/helpers/lang'
import {mapValueKeys} from '@shared/helpers/object'

import {getProductItemCouponDiscounts, getProductItemCouponDiscountByC1} from '@web/helpers/policies'
import {DeliveryGroupState} from '@web/slices/deliveryGroup/slice'
import {MerchantsState} from '@web/slices/merchants/slice'
import {
    duplicateCouponInfoSelector,
    OrderSheetCoupon,
    OrderSheetCouponState,
    productCouponInfoSelector,
} from '@web/slices/orderSheetCoupon/slice'
import {orderSheetProductInfoSelector, OrderSheetProductsState} from '@web/slices/orderSheetProducts/slice'
import {OrderTotalState} from '@web/slices/orderTotal/slice'

type RootState = {
    merchants: MerchantsState
    deliveryGroup: DeliveryGroupState
    orderSheetProducts: OrderSheetProductsState
    orderSheetCoupon: OrderSheetCouponState
    orderTotal: OrderTotalState
}

/**
 * 쿠폰 할인 금액을 계산하여 반영
 * - 추후 C1 로직이 BE 로 이관되면 제거
 **/
function makeProductDiscountItem(
    state: RootState,
    {
        itemId,
        selectItemCoupons,
    }: {
        itemId: Maybe<string>
        selectItemCoupons: ProductCoupons
    },
): OrderSheetCouponState['discount']['productItem'] {
    const {orderSheetItemFlatMap} = orderSheetProductInfoSelector(state)
    const {selectedCoupons: selectedProductCoupons} = productCouponInfoSelector(state)
    const {selectedCoupons: selectedDuplicateCoupons} = duplicateCouponInfoSelector(state)
    return mapValueKeys((orderPriceInfo, orderSheetItemID) => {
        // 이전 선택 쿠폰
        const selectedCoupons = [
            ...(selectedProductCoupons[orderSheetItemID] ?? []),
            ...(selectedDuplicateCoupons[orderSheetItemID] ?? []),
        ]
        const coupons = itemId === orderSheetItemID ? selectItemCoupons : selectedCoupons
        if (!isEmpty(coupons)) {
            /**
             * unitPrice, optionPrice 대신 orderAmount를 사용하는 이유
             * - C1 1.0의 경우 옵션가가 도입되어 있지 않아 주문금액에 옵션가를 포함하여 전달하고 있다.
             * - C1 2.1 및 스스부터 unitPrice, optionPrice 를 사용하는게 유효하다.
             */
            const discountInfos = getProductItemCouponDiscounts({
                orderAmount: orderPriceInfo.orderAmount,
                coupons,
            })

            return discountInfos.map(({discountAmount, discountType, discountProviderType, couponType}) => {
                return {
                    baseAmount: orderSheetItemFlatMap[orderSheetItemID]?.orderAmount ?? 0,
                    discountAmount,
                    discountType,
                    discountProviderType,
                    couponType,
                }
            })
        }
        return []
    }, orderSheetItemFlatMap)
}

type ProductCoupons = OrderSheetCoupon[]

/**
 * 상품 쿠폰 선택 Action
 * - ( FE 에서 쿠폰 직접 계산 버전 )
 **/
export const selectProductItemCoupons = createAsyncThunk<
    {
        selectProductCoupons: ProductCoupons
        selectDuplicateCoupons: ProductCoupons
        itemId: string
        /** 상품별 적용된 할인 정보 */
        discount: OrderSheetCouponState['discount']
    },
    {
        selectProductCoupons: ProductCoupons
        selectDuplicateCoupons: ProductCoupons
        itemId: string
    },
    {
        state: RootState
        rejectValue: {
            message: string
        }
    }
>(
    'productCoupon/selectProductItemCoupons',
    ({selectProductCoupons, selectDuplicateCoupons, itemId}, {getState, rejectWithValue}) => {
        const state = getState()
        const {orderSheetItemFlatMap} = orderSheetProductInfoSelector(state)

        const {orderAmount} = orderSheetItemFlatMap[itemId] ?? {
            orderAmount: 0,
        }

        // 선택한 쿠폰
        const selectItemCoupons = [...selectProductCoupons, ...selectDuplicateCoupons]

        try {
            if (
                /**
                 * unitPrice, optionPrice 대신 orderAmount를 사용하는 이유
                 * - C1 1.0의 경우 옵션가가 도입되어 있지 않아 주문금액에 옵션가를 포함하여 전달하고 있다.
                 * - C1 2.1 및 스스부터 unitPrice, optionPrice 를 사용하는게 유효하다.
                 */
                orderAmount -
                    getProductItemCouponDiscountByC1({
                        orderAmount: orderSheetItemFlatMap[itemId]?.orderAmount ?? 0,
                        coupons: selectItemCoupons,
                    }) <
                0
            ) {
                return rejectWithValue({
                    message:
                        '선택하신 쿠폰 적용 시, 주문금액이 0원 미만이 되어 기존 선택된 쿠폰 중 일부 쿠폰을 적용할 수 없습니다.',
                })
            }
        } catch (e) {
            if (e instanceof Error) {
                return rejectWithValue({
                    message: e.message,
                })
            }
        }

        return {
            itemId,
            /** 상품할인 쿠폰 선택 */
            selectProductCoupons,
            /** 중복할인 쿠폰 선택 */
            selectDuplicateCoupons,
            /** 상품별 적용된 할인 정보 */
            discount: {
                productItem: makeProductDiscountItem(state, {
                    itemId,
                    selectItemCoupons,
                }),
            },
        }
    },
)

/** 상품 쿠폰 선택 */
export const selectProductCoupon = createAction<{
    itemId: string
    /** 상품할인 쿠폰 선택 */
    couponIds: string[]
    /** 중복할인 쿠폰 선택 */
    couponIdsByDuplicate: string[]
    /** 즉시할인 쿠폰 선택 */
    policyNos: string[]
    /** 선택 해지 인지 확인 */
    isRemove: boolean
    /** 선택된 프로모션이 있는지 확인 */
    isSelectedPromotionGroupExist?: boolean
}>('orderSheetCoupon/selectProductCoupon')

/** 선택하려고하는 쿠폰 적용 */
export const updateSelectCoupon = createAction('orderSheetCoupon/updateSelectCoupon')

/** 선택하려고 했던 쿠폰 취소 */
export const cancelSelectCoupon = createAction('orderSheetCoupon/cancelSelectCoupon')

/** 배송비 쿠폰 선택 */
export const selectDeliveryCoupon = createAction<{
    deliveryGroupKey: string
    couponId: Maybe<string>
    /** 선택 해지 인지 확인 */
    isRemove: boolean
}>('orderSheetCoupon/selectDeliveryCoupon')

/** 선택된 쿠폰 정보 modal로 복사 */
export const startSimulateCoupon = createAction('orderSheetCoupon/startSimulateCoupon')

/** 쿠폰 페이지 > 상품 쿠폰 선택 */
export const selectProductCouponPage = createAction<{
    itemId: string
    /** 상품할인 쿠폰 선택 */
    couponIds: string[]
    /** 중복할인 쿠폰 선택 */
    couponIdsByDuplicate: string[]
    /** 즉시할인 쿠폰 선택 */
    policyNos: string[]
    /** 복수구매 할인 선택 */
    multiPurchaseDiscounts: string[]
    /** 선택 해지 인지 확인 */
    isRemove: boolean
    /** 선택된 프로모션이 있는지 확인 */
    isSelectedPromotionGroupExist?: boolean
}>('orderSheetCoupon/selectProductCouponPage')

/** 쿠폰 페이지 > 배송비 쿠폰 선택 */
export const selectDeliveryCouponPage = createAction<{
    deliveryGroupKey: string
    couponId: Maybe<string>
    /** 선택 해지 인지 확인 */
    isRemove: boolean
    /** 할인금액 검증 여부 */
    shouldDiscountAmountReload: boolean
}>('orderSheetCoupon/selectDeliveryCouponPage')

/** 쿠폰 페이지 > 스토어 쿠폰 선택 */
export const selectStoreCouponPage = createAction<{
    merchantNo: number
    couponId: Maybe<string>
    /** 선택 해지 인지 확인 */
    isRemove: boolean
}>('orderSheetCoupon/selectStoreCouponPage')

export const applySimulateCouponPage = createAction<{
    isSelectingChanged?: boolean
}>('orderSheetCoupon/applySimulateCouponPage')

export const cancelSimulateCouponPage = createAction('orderSheetCoupon/cancelSimulateCouponPage')

export const removeCouponPageBlock = createAction('orderSheetCoupon/removeCouponPageBlock')
